import { useEffect, useMemo } from 'react';

import {
  useStreamRepositoryInjector,
  useBillingRepositoryInjector,
  usePointRepositoryInjector,
} from 'application/providers/DIContainerProvider';
import { useAccountContext } from 'application/providers/AccountProvider';
import { useFeatureFlagsContext } from 'application/providers/FeatureFlagsProvider';

import useUserFeaturesOnboardingPass from 'domain/media/useUserFeaturesOnboardingPass';
import useUserFeaturesOnboarding from 'domain/media/useUserFeaturesOnboarding';
import { Country, useBillingPaymentMethodQuery, UserFeatures } from 'domain/api/graphql/generated';
import { FetchPolicyKeys } from 'domain/api/graphql/types';

import { PLAYER_TEXT_TYPE, PlayerRadioType, PlayerTypesInFlow } from './types';

const useSelectPlayerModalViewModel = () => {
  const {
    isChangeStreamLoading,
    isCopyStreamTemplateLoading,
    isSavedStreamsLoading,
    changeStream,
    copyTemplateStream,
    getSavedStreams,
  } = useStreamRepositoryInjector();

  const { getBillingInfo, watchBillingInfo } = useBillingRepositoryInjector();
  const { pointSetDevice, readCurrentPoint, setCurrentPoint, clearCurrentPoint } = usePointRepositoryInjector();

  const { currentCompany } = useAccountContext();
  const { isVideoAvailable } = useFeatureFlagsContext();

  const { request: onboardingPassedRequest, result } = useUserFeaturesOnboardingPass();
  const { refetch: refetchFeaturesOnboarding } = useUserFeaturesOnboarding({
    features: [UserFeatures.WarningStreamFirstStart],
  });

  const isBillingEnabled = currentCompany?.isBillingEnabled;

  const { data } = useBillingPaymentMethodQuery({
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    variables: { companyId: currentCompany!.id },
  });

  const { hasActiveLicenses, hasInactiveLicenses } = watchBillingInfo(currentCompany!.id);

  const isNeedPayment = (hasActiveLicenses && !data?.paymentMethodPureQuery?.paymentMethod) || hasInactiveLicenses;

  useEffect(() => {
    if (isBillingEnabled) {
      getBillingInfo(currentCompany?.id!);
    }
  }, []);

  const playersData = useMemo(() => {
    const players = [] as PlayerRadioType[];

    for (const type in PlayerTypesInFlow) {
      if (Object.prototype.hasOwnProperty.call(PlayerTypesInFlow, type)) {
        const playerType = type as PlayerTypesInFlow;

        const hideDevice =
          PLAYER_TEXT_TYPE[playerType].ruByOnly &&
          !(currentCompany?.country === Country.Ru || currentCompany?.country === Country.By);

        const isHideVideoDevice = playerType === PlayerTypesInFlow.VIDEOPLAYER && !isVideoAvailable;

        if (!hideDevice && !isHideVideoDevice) {
          players.push({
            title: PLAYER_TEXT_TYPE[playerType].title,
            description: PLAYER_TEXT_TYPE[playerType].description,
            type: playerType,
          });
        }
      }
    }

    return players;
  }, []);

  return {
    playersData,
    isChangeStreamLoading,
    isCopyStreamTemplateLoading,
    isSavedStreamsLoading,
    isUserFeaturesOnboardingPassLoading: result.loading,
    isNeedPayment,
    isBillingEnabled,
    changeStream,
    copyTemplateStream,
    getSavedStreams,
    onboardingPassedRequest,
    refetchFeaturesOnboarding,
    pointSetDevice,
    readCurrentPoint,
    setCurrentPoint,
    clearCurrentPoint,
  };
};

export default useSelectPlayerModalViewModel;
